export interface YMD {
  y: number;
  m: number;
  d: number;
}

export type TxnType = 'credit' | 'debit';

export interface Txn {
  d: YMD;
  name: string;
  amt: number;
  type: TxnType;
  cat: string;
  paid: boolean;
  autopay: boolean;
  rec: string | null;
  itemId?: string;
}

export interface LedgerRow extends Txn {
  id: string;
  balance: number;
  delta: number;
}

export interface DayGroup {
  d: YMD;
  rows: LedgerRow[];
}

export const STARTING_BALANCE = 1842.5;
export const TODAY: YMD = { y: 2026, m: 5, d: 23 };

export function todayYmd(): YMD {
  const d = new Date();
  return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate() };
}

export const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
export const MONTH_SHORT = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function fmtMoney(n: number): string {
  const neg = n < 0;
  const abs = Math.abs(n);
  const s = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (neg ? '−$' : '$') + s;
}

export function fmtCents(n: number): string {
  return Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Compact money for tight stat cells. Below `threshold` it's the full
// fmtMoney form; at or above it collapses the magnitude to an abbreviated
// "~$27.3M" style (K / M / B / T) so big balances don't overflow or collide.
export function fmtMoneyCompact(n: number, threshold = 1_000_000): string {
  const abs = Math.abs(n);
  if (abs < threshold) return fmtMoney(n);
  const units: [number, string][] = [
    [1e12, 'T'],
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
  ];
  const sign = n < 0 ? '−' : '';
  for (const [size, suffix] of units) {
    if (abs >= size) {
      // One decimal, dropping a trailing ".0" (e.g. 27.0M → 27M).
      const scaled = (abs / size).toFixed(1).replace(/\.0$/, '');
      return `${sign}~$${scaled}${suffix}`;
    }
  }
  return fmtMoney(n);
}

export function formatRelativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, Math.floor((now.getTime() - then) / 1000));
  if (diffSec < 60) return 'just now';
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.floor(day / 365);
  return `${yr}y ago`;
}

export function dateKey(d: YMD): string {
  return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
}

export function dowOf(d: YMD): number {
  return new Date(d.y, d.m - 1, d.d).getDay();
}

export function daysInMonth(y: number, m: number): number {
  return new Date(y, m, 0).getDate();
}

export function isSameDay(a: YMD, b: YMD): boolean {
  return a.y === b.y && a.m === b.m && a.d === b.d;
}

export function cmpDates(a: YMD, b: YMD): number {
  return a.y - b.y || a.m - b.m || a.d - b.d;
}

export function addDays(d: YMD, days: number): YMD {
  const dt = new Date(d.y, d.m - 1, d.d);
  dt.setDate(dt.getDate() + days);
  return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
}

// Projected balance on a given date: running balance of the last row whose
// scheduledDate is on or before `on`. Falls back to `fallback` when no rows
// land on/before that date.
export function projectedBalanceOn(
  rows: LedgerRow[],
  on: YMD,
  fallback: number,
): number {
  let bal = fallback;
  for (const r of rows) {
    if (cmpDates(r.d, on) <= 0) bal = r.balance;
    else break;
  }
  return bal;
}

export function cmpTxnsWithinDay(a: Txn, b: Txn): number {
  if (a.type !== b.type) return a.type === 'credit' ? -1 : 1;
  const aRecurring = a.rec !== null;
  const bRecurring = b.rec !== null;
  if (aRecurring !== bRecurring) return aRecurring ? -1 : 1;
  return a.name.localeCompare(b.name);
}

export function buildLedger(txns: Txn[], startBalance = STARTING_BALANCE): LedgerRow[] {
  const sorted = [...txns].sort((a, b) => {
    const byDate = cmpDates(a.d, b.d);
    if (byDate !== 0) return byDate;
    return cmpTxnsWithinDay(a, b);
  });
  let bal = startBalance;
  return sorted.map((t, i) => {
    const delta = t.type === 'credit' ? t.amt : -t.amt;
    bal += delta;
    return { ...t, id: `tx-${i}`, balance: bal, delta };
  });
}

export function groupByDay(rows: LedgerRow[]): DayGroup[] {
  const days: Record<string, DayGroup> = {};
  for (const r of rows) {
    const key = dateKey(r.d);
    if (!days[key]) days[key] = { d: r.d, rows: [] };
    days[key].rows.push(r);
  }
  return Object.values(days);
}

export function buildCalendarCells(y: number, m: number): Array<YMD | null> {
  const startDow = new Date(y, m - 1, 1).getDay();
  const days = daysInMonth(y, m);
  const cells: Array<YMD | null> = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push({ y, m, d });
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}
